import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import BackArrow from "../assets/images/back-arrow.svg";
import Clock from "../assets/icons/clock1.svg";
import LocationPin from "../assets/icons/pin.svg";
import EditLocation from "../assets/icons/edit.svg";
import LocationBtnIcon from "../assets/images/location-btn-icon.png";
import RhinitisImg from "../assets/images/rhinitis-img.png";
import SearchIcon from "../assets/images/search-icon-white.svg";
import LocationIcon from "../assets/images/current-location.svg";
import {
  allergy_ai_api_key,
  allergy_ai_api_secret_key,
} from "../utils/api-url-list";

const AllergyDashboard = () => {
  const [visible, setVisible] = useState(true);
  const [location, setLocation] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEnvLoading, setEnvLoading] = useState(false);
  const [geoLocLoading, setGeoLoacLoading] = useState(false);
  const [error, setError] = useState("");
  const [environmentalData, setEnvironmentalData] = useState({});

  const COMMON_CITIES = [
    "New York, USA",
    "London, UK",
    "Tokyo, Japan",
    "Paris, France",
    "Sydney, Australia",
    "Toronto, Canada",
    "Singapore",
    "Dubai, UAE",
    "Mumbai, India",
    "São Paulo, Brazil",
  ];

  useEffect(() => {
    handleGeolocation();
  }, []);

  const handleGeolocation = async () => {
    try {
      const coords = await new Promise((resolve) => {
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(
            (pos) =>
              resolve({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              }),
            (err) => {
              console.error(`Geo-Location Error(${err.code}): ${err.message}`);
              resolve({ latitude: null, longitude: null });
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
          );
        } else {
          console.error("Geo-Location APIs are unavailable");
          resolve({ latitude: null, longitude: null });
        }
      });
      if (!coords.latitude || !coords.longitude) {
        setError(
          "Could not get your current location. Please enter it manually."
        );
        setStatus("error");
        setGeoLoacLoading(false);
        return;
      }
      setGeoLoacLoading(true);
      setStatus("validating_location");
      const response = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: allergy_ai_api_secret_key,
          },
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "location_analysis",
            allergy_assessment_id: "",
            location: coords,
            file_url: "",
          }),
        }
      );

      const currentLocResp = await response.json();
      console.log("API Response:", currentLocResp);
      if (
        currentLocResp?.statusCode?.toString().startsWith("2") &&
        currentLocResp.result.isValid
      ) {
        const finalLocation = currentLocResp?.result?.standardized;
        setStatus("success");
        setSuggestions([]);
        setLocation(finalLocation);
        getEnvironmentData(finalLocation);
      } else {
        setError(
          currentLocResp?.result?.suggestion || "Please enter a valid location"
        );
        setStatus("error");
      }
    } catch (err) {
      console.error("Error in handleGeolocation:", err);
      setStatus("error");
    } finally {
      setGeoLoacLoading(false);
    }
  };

  const locationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    setError("");
    if (value.length > 1) {
      const matches = COMMON_CITIES.filter((city) =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(matches.slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const getEnvironmentData = async (loc) => {
    setStatus("fetching_env_data");
    setEnvLoading(true);
    try {
      const response = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: allergy_ai_api_secret_key,
          },
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "environment_analysis",
            allergy_assessment_id: "",
            location: loc,
            file_url: "",
          }),
        }
      );
      const EnvRespJSON = await response.json();
      if (EnvRespJSON?.statusCode?.toString().startsWith("2")) {
        setEnvironmentalData(EnvRespJSON?.result);
        setVisible(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setEnvLoading(false);
      setStatus("");
    }
  };

  const getLocation = async (locationString = location) => {
    if (!locationString.trim()) {
      setError("Please enter a location");
      return;
    }

    setStatus("validating_location");
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: allergy_ai_api_secret_key,
          },
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "location_analysis",
            allergy_assessment_id: "",
            location: locationString,
            file_url: "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to validate location");
      }

      const locationResp = await response.json();
      if (
        locationResp?.statusCode?.toString().startsWith("2") &&
        locationResp.result.isValid
      ) {
        const finalLocation =
          locationResp?.result?.standardized || locationString;
        setStatus("success");
        setSuggestions([]);
        setLocation(finalLocation);
        getEnvironmentData(finalLocation);
      } else {
        setError(
          locationResp?.result?.suggestion || "Please enter a valid location"
        );
        setStatus("error");
      }
    } catch (error) {
      console.error("Error validating location:", error);
      setError("Unable to validate location. Please try again.");
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setLocation(suggestion);
    setSuggestions([]);
    getLocation(suggestion);
  };
  return (
    <section className="pt-2 pb-14 flex flex-col">
      <div className="px-6 tab-screen">
        <div className="flex items-center justify-between mb-8 w-full relative z-50">
          <Link
            to={-1}
            className=" w-9 h-9 bg-[#2D2D2D] rounded-full flex flex-row justify-center items-center"
          >
            <img src={BackArrow} alt="" />
          </Link>
          <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white">
            Allergy<span className="text-secondary">Ai</span>
          </h2>
          <img src={Clock} className="w-8 h-8" alt="" />
        </div>
        <div className="score-card2 px-5 py-5 w-full bg-[#1D1D1D]/70 relative z-50 rounded-[20px] mb-6">
          <div className="flex flex-row justify-between items-center">
            <h3 className="text-xl font-semibold">
              Allergen
              <br /> Resilience Score
            </h3>
            <button className="view-btn text-xs">View Details</button>
          </div>
          <p className=" text-xxs text-white/70 text-justify mt-2">
            The Allergen Resilience Score (0–100) reflects your body’s ability
            to withstand allergen triggers—100 denotes top resilience. It blends
            thermal mapping, nasal airflow metrics, and recent exposure history
            to assess your defensive response.
          </p>
          <div className="progress-col relative mt-12 mb-3">
            {/* <h3 className="progress-title" style={{ left: "85%" }}>
                  <span>85</span>
                </h3> */}
            <div className="progress bg-gradient-to-r from-[#C81616] via-[#FFC444] to-[#64BD40]">
              <div class="progress-bar" style={{ width: "62%" }}>
                <div class="progress-value">62</div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full relative">
          <div className="circle-blur w-[70%] h-64 mx-auto absolute left-0 right-0 -top-14 blur-[100px]"></div>
          <div className="bg-[#1D1D1D]/70 rounded-[20px] px-6 py-5 relative z-50 lg:mb-3 md:mb-3">
            {visible ? (
              <div className="serach-location">
                <div className="flex flex-row justify-between">
                  <div className="relative w-10/12">
                    <span className=" absolute left-3 top-[28px]">
                      <img
                        src={LocationPin}
                        className="w-4 h-4 opacity-50"
                        alt=""
                      />
                    </span>
                    <input
                      id="location"
                      name="location"
                      className={`block w-full mt-4 pl-10 pr-10 py-2 rounded-lg input-col border border-[#fff]/30 focus:border-secondary bg-transparent text-white text-sm ${
                        isLoading || isEnvLoading || geoLocLoading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="Enter City Name"
                      type="text"
                      value={location}
                      onChange={locationChange}
                      disabled={isLoading || isEnvLoading || geoLocLoading}
                    />
                    <button
                      className={`absolute right-3 top-[25px] ${
                        isLoading || isEnvLoading || geoLocLoading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      onClick={handleGeolocation}
                      disabled={isLoading || isEnvLoading || geoLocLoading}
                    >
                      <img src={LocationIcon} className=" w-5 h-5" alt="" />
                    </button>
                  </div>
                  <button
                    className={`bg-secondary rounded-md px-2 h-9 mt-4 ${
                      isLoading || isEnvLoading || geoLocLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() => getLocation()}
                    disabled={isLoading || isEnvLoading || geoLocLoading}
                  >
                    <img src={SearchIcon} className="w-5 h-5" alt="" />
                  </button>
                </div>
                <div>
                  {suggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-slate-200">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm text-slate-700 first:rounded-t-md last:rounded-b-md"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}

                  {error && (
                    <div className="text-xxs text-error text-justify mt-3 leading-4 font-light">
                      {error}
                    </div>
                  )}

                  {!error && status && (
                    <div className="text-xxs text-white text-justify mt-3 leading-4 font-light">
                      {status === "detecting" && "Detecting your location..."}
                      {status === "validating_location" &&
                        "Validating your location..."}
                      {status === "fetching_env_data" &&
                        "Fetching environmental data..."}
                    </div>
                  )}
                </div>
                <div className="flex flex-row justify-center items-center">
                  <button
                    className="h-8 px-6 bg-white text-primary rounded-full text-sm flex flex-row items-center justify-center mt-8 mb-4"
                    onClick={() => setVisible(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-row">
                <img src={LocationPin} className=" w-4 h-4 mt-1" alt="" />
                <div className="ml-2">
                  <div className="flex flex-row justify-between">
                    <h4 className="text-base font-semibold text-secondary">
                      {location}
                    </h4>
                    <button
                      className="w-6 h-6 bg-secondary rounded-full flex flex-row items-center justify-center"
                      onClick={() => setVisible(true)}
                    >
                      <img src={EditLocation} className="w-4 h-4" alt="" />
                    </button>
                  </div>
                  <p className="text-xxs text-white text-justify mt-3 leading-4 font-light">
                    {environmentalData?.weather_summary ??
                      "No environment data found. Click the location icon to detect your current location or add it manually."}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="location-map mt-4 px-6 pt-8">
        <div className="tab-screen">
          <div className="location-btn relative">
            <div
              className={`location-blur w-24 h-24 blur-[50px] rounded-full mx-auto absolute top-16 left-[35%] ${
                environmentalData?.aqi <= 50
                  ? "bg-gradient-to-t from-[#64BD40]/80 to-[#2ECC71]/80"
                  : environmentalData?.aqi <= 100
                  ? "bg-gradient-to-t from-[#FFC444]/80 to-[#FFB200]/80"
                  : "bg-gradient-to-t from-[#F36113]/80 to-[#ED1E13]/80"
              }`}
            ></div>

            <div className="flex justify-between relative z-50">
              <button className="flex flex-row items-center justify-between border border-secondary text-white text-xs font-medium rounded-full h-9 pr-4 pl-2 ml-3">
                <img src={LocationBtnIcon} className="mr-2" alt="" />
                AQI
                <span className="text-secondary ml-2">
                  {environmentalData?.aqi ?? "--"}
                </span>
              </button>
              <button className="flex flex-row items-center justify-between border border-secondary text-white text-xs font-medium rounded-full h-9 pr-4 pl-2 mt-4 mr-8">
                <img src={LocationBtnIcon} className="mr-2" alt="" />
                PM2
                <span className="text-secondary ml-2">
                  {environmentalData?.pm2_5 ?? "--"}
                </span>
              </button>
            </div>
            <div className="mt-4 relative z-50">
              <button className="flex flex-row items-center justify-between border border-secondary text-white text-xs font-medium rounded-full h-9 pr-4 pl-2 ml-14">
                <img src={LocationBtnIcon} className="mr-2" alt="" />
                PM10
                <span className="text-secondary ml-2">
                  {environmentalData?.pm10 ?? "--"}
                </span>
              </button>
            </div>
            <div className="mt-4 flex flex-row items-end w-10/12 justify-end relative z-50">
              <button className="flex flex-row items-center justify-between border border-secondary text-white text-xs font-medium rounded-full h-9 pr-4 pl-2 ml-14">
                <img src={LocationBtnIcon} className="mr-2" alt="" />
                Humidity
                <span className="text-secondary ml-2">
                  {environmentalData?.humidity ?? "--"}
                </span>
              </button>
            </div>
            <div className="mt-5 relative z-50">
              <button className="flex flex-row items-center justify-between border border-secondary text-white text-xs font-medium rounded-full h-9 pr-4 pl-2 ml-10">
                <img src={LocationBtnIcon} className="mr-2" alt="" />
                Temperature
                <span className="text-secondary ml-2">
                  {environmentalData?.temperature ?? "--"}°C
                </span>
              </button>
            </div>
          </div>
          <div className="mt-8 relative">
            <div className="absolute left-0 right-0 top-6">
              <h2 className="w-7/12 lg:mx-auto md:mx-auto text-[28px] lg:text-4xl md:text-4xl font-semibold ml-6 text-white leading-8 lg:text-center md:text-center">
                Stay Informed about your Allergy Rhinitis risk
              </h2>
              <Link
                className="mt-6 w-9/12 mx-auto rounded-full outline-none px-5 h-12 bg-white text-primary flex flex-row justify-center items-center"
                to="/new-assessment"
                replace
                state={{ location, environmentalData }}
              >
                Take Assessment
              </Link>
            </div>
            <img src={RhinitisImg} className="w-full" alt="" />
          </div>
        </div>
      </div>

      {/* <Link to={-1} className="block mt-4 text-sm font-medium  text-white">
      Back
    </Link> */}
    </section>
  );
};

export default AllergyDashboard;
